import { useEffect } from 'react';
import { LayoutWrapper } from '../../layouts';
import { Header } from './components/header';
import { WorkerJobApplications } from './components/worker-job-applications';
import { workerApplicationDataLoadingAtom } from './global-state';
import { useAtomValue } from 'jotai';
import { DefaultLoadingUi } from '../restaurant-showcase';
import { useLoadWorkerApplications } from './hooks/use-load-worker-applications';
import { ProposedJobApplications, proposedWorkerJobApplicationsAtom } from './components/proposed-job-applications';
import { ViewOtherPositionsButton } from './components/job-application-tab-content';
import { useAuth } from '@restworld/feature-authentication';
import { useTranslation } from 'next-i18next';
import { showToast } from '@restworld/ui-ds-base';
import { useRouter } from 'next/router';
import { cn } from '@restworld/utils-common';

import { ExternalResourceCard } from '../external-resource-card';

/**
 * Rights hub on the marketing site. Verified final URL: responds 200 with no
 * redirect. The `/it/` variant 307s, so it must not be used here.
 */
const WORKER_RIGHTS_URL = 'https://www.restworld.it/diritti';

export const JobApplicationComponent = () => {
  const { user, isAuthenticated } = useAuth();
  const isLoading = useAtomValue(workerApplicationDataLoadingAtom);
  const { fetchWorkerApplications } = useLoadWorkerApplications({ showLoader: true });
  const { t } = useTranslation('job-application-page');
  const router = useRouter();
  const proposedJobApplications = useAtomValue(proposedWorkerJobApplicationsAtom);

  useEffect(() => {
    fetchWorkerApplications();
  }, [fetchWorkerApplications]);

  // Show inactive job_position associated proposed job_application error
  useEffect(() => {
    if (isLoading) return;
    const job_position_id = router?.query?.job_position_id;
    if (job_position_id) {
      const jobPosition = proposedJobApplications?.find((job) => job.jobPositionId === job_position_id);
      if (!jobPosition) {
        showToast(t('inactive_job_position_proposed_application'), 'error', {
          position: 'top-right',
          autoClose: 3000,
          toastId: `job_position_${job_position_id}_not_available`,
        });
        const queryParams = { ...(router.query ?? {}) };
        delete queryParams.action;
        delete queryParams.job_position_id;
        delete queryParams.worker_id;
        router.push({ query: queryParams }, undefined, { shallow: true });
      }
    }
  }, [proposedJobApplications, router, t, isLoading]);

  if (isLoading) {
    return <DefaultLoadingUi />;
  }

  return (
    // Plain page like the employer app: neutral-10 everywhere, the layout's own paddings,
    // no purple gradient behind the title on mobile.
    <LayoutWrapper
      hasFooter
      hasNavbar
      title={t('nominations')}
      showSearchBarInNavbar={isAuthenticated ? user?.type !== 'employer' : true}
      // The page colour has to be painted: `html` and `body` are transparent in this app,
      // so without this the browser canvas (white) shows through, not neutral-10.
      className="min-h-screen bg-neutral-10"
    >
      <JobApplicationWrapper>
        <Container>
          <JobApplicationHeader>
            <Header />
          </JobApplicationHeader>
          <BodyWrapper>
            <WorkerJobApplications />
            {/* One block after the worker's own applications: everything here is "discover
                new jobs" — what we recommend, then the search. The rule above separates it
                from the list. Recommended jobs used to open the page on mobile. */}
            <DiscoverJobsSection>
              <ProposedJobApplications />
              <ViewOtherPositionsButton />
            </DiscoverJobsSection>
            {/* Below the whole tab block, not inside a tab: the list is the content,
                the resource is what comes after it. */}
            <div className="w-full mt-8">
              <ExternalResourceCard
                title={t('rights_resource_card_title')}
                href={WORKER_RIGHTS_URL}
                ctaLabel={t('rights_resource_card_cta')}
              />
            </div>
          </BodyWrapper>
        </Container>
      </JobApplicationWrapper>
    </LayoutWrapper>
  );
};

export const JobApplicationWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div
    className={cn('flex box-border lg:flex-row lg:justify-center flex-col  max-w-full overflow-x-hidden', className)}
    {...props}
  />
);

export const Container = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex flex-col lg:gap-0 items-start max-w-full w-full', className)} {...props} />
);

export const JobApplicationHeader = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex flex-col w-full gap-0', className)} {...props} />
);

export const DiscoverJobsSection = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div
    className={cn('flex flex-col items-center gap-8 w-full mt-8 pt-8 border-t border-neutral-40', className)}
    {...props}
  />
);

export const BodyWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex flex-col w-full items-start lg:mt-10 mt-6', className)} {...props} />
);

export * from './application-details';
